import { signal, computed, batch, type Signal } from "@preact/signals-core";
import type {
  Bounds,
  ContextMenuState,
  DragSelectState,
  DxfUnit,
  Entity,
  HistoryEntry,
  Layer,
  ModalState,
  Pan,
  SidebarPosition,
  Theme,
  ToastEntry,
} from "./types.js";

// Fine-grained signals for every independently-reactive slice. Keeping them
// separate (rather than one god-signal) means a change to `pan` doesn't
// re-run every layer-panel effect.
//
// External code never touches these directly — use the reader helpers below
// and mutate through functions in `ops/`.

// ---- Model ----
const _entities = signal<Entity[]>([]);
const _layers = signal<Record<string, Layer>>({});
const _layerOrder = signal<string[]>([]);
const _selectedEntityIds = signal<Set<number>>(new Set());
const _selectedLayerNames = signal<Set<string>>(new Set());
const _selectedLayer = signal<string | null>(null);
const _nextEntityId = signal<number>(0);
const _originalDxf = signal<string | null>(null);
const _filename = signal<string | null>(null);

// HEADER $CUSTOMPROPERTY pairs found on the loaded DXF. Used to detect
// DXF_HEALED (so the Fix button hides on already-healed files) and to
// preserve arbitrary custom vars across a round-trip through the exporter.
const _customVars = signal<Record<string, string>>({});
// The $INSUNITS code found on the loaded DXF, remembered so an export can
// detect when the user has since changed the editor's dxfUnitCode and stamp
// a DXF_UNIT_OVERRIDDEN tag.
const _loadedUnitCode = signal<number | null>(null);

// ---- Auto-heal + auto-GLB pipeline ----
// Whenever a DXF is loaded the editor kicks off a two-stage background
// pipeline: (1) POST /heal and swap in the cleaned doc, (2) POST
// /convert-to-glb with the cleaned doc and stash the resulting GLB as a blob
// URL. The canvas locks with a progress ring during (1); the topbar shows a
// small indicator during (2); once glbUrl is set the 3D-cube button in the
// topbar activates and clicking it switches viewMode from '2d' to '3d'.
const _healActive = signal<boolean>(false);
const _healProgress = signal<number>(0); // 0..1, indeterminate when 0 during active
const _glbActive = signal<boolean>(false);
const _glbProgress = signal<number>(0);  // 0..1, driven by SSE checkpoints
const _glbUrl = signal<string | null>(null);
const _viewMode = signal<"2d" | "3d">("2d");
// Bumped by ops/ui.fitCurrentView so the currently-active view (canvas or
// view-3d) can re-fit its camera on demand. Both views subscribe via effect;
// increment-only, value semantics don't matter.
const _resetSeq = signal<number>(0);

// ---- Units + grid ----
// Editor-specific defaults: inches with a 1in grid. Loading a DXF does NOT
// change these — the coordinates in the file are interpreted as whatever the
// editor is set to, and exports go out with the same unit. To work with a
// mm-authored file, change units via the Units & Grid Settings modal.
// dxfUnit is a LABEL over the raw entity coordinates ("what unit these
// numbers mean"). viewerUnit is the user's preferred display unit for the
// top-bar dimensions. When they differ, the top bar converts on the fly
// via mmPerUnit(dxfUnit) / mmPerUnit(viewerUnit); entity coords are never
// rescaled in memory.
const _dxfUnit = signal<DxfUnit>("in");
const _dxfUnitCode = signal<number>(1);
const _viewerUnit = signal<DxfUnit>("in");
const _gridUnit = signal<DxfUnit>("in");
const _gridSpacing = signal<number>(1);
const _showGrid = signal<boolean>(true);
const _showGridRuler = signal<boolean>(true);

// ---- View ----
// Default zoom 50 = 50 px per drawing unit. At the editor's default 1-inch
// gridSpacing that's a 50px grid, comfortably above the renderer's minor-tick
// threshold (~20px) and the ruler threshold (~6px), so both are visible on
// the empty canvas before any DXF is loaded.
const _zoom = signal<number>(50);
// The raw zoom value that reads as "100%" in the status bar. Starts equal to
// the default zoom so an empty editor shows 100%; fitToBounds rewrites it so
// a freshly-fit drawing also shows 100%. Everything else (wheel zoom, etc.)
// is displayed relative to this.
const _referenceZoom = signal<number>(50);
const _pan = signal<Pan>({ x: 0, y: 0 });
const _bounds = signal<Bounds | null>(null);
const _isLoaded = signal<boolean>(false);
const _viewSeq = signal<number>(0); // bumped on any view-affecting change

// ---- Chrome ----
const _theme = signal<Theme>("dark");
const _filterText = signal<string>("");
const _sidebarPosition = signal<SidebarPosition>("right");
// Hidden by default — the topbar's Layers toggle is disabled until a DXF
// loads, at which point loadDxfFromString flips this to true.
const _sidebarVisible = signal<boolean>(false);

// Viewport width in CSS pixels of the host element, refreshed by
// dxf-editor.ts on ResizeObserver ticks. Downstream components subscribe via
// `isMobile()` — anything below the breakpoint triggers touch-friendly chrome
// (bottom-sheet layer panel, kebab overflow topbar, full-screen modals).
const _hostWidth = signal<number>(
  typeof window !== "undefined" ? window.innerWidth : 1200
);
// Kept in sync with CSS @media (max-width: 720px) so the two decision paths
// (JS behavior + CSS layout) never disagree.
const MOBILE_BREAKPOINT = 720;

// ---- UI-transient ----
// Persistent toast queue: entries stay until the user dismisses them.
// _toastsExpanded toggles the stack between the collapsed (top-of-stack
// preview) and the expanded (up to 6 visible, rest scrollable) views.
const _toasts = signal<ToastEntry[]>([]);
const _toastsExpanded = signal<boolean>(false);
const _nextToastId = signal<number>(1);
const _modal = signal<ModalState>({ open: false, kind: null, data: {} });
const _contextMenu = signal<ContextMenuState>({ open: false, x: 0, y: 0 });
// One-shot signal: when set to a layer name, the layer panel enters inline
// rename mode for that layer on its next render and clears this back to
// null. Lets the layer context menu ("Rename Layer") request an inline
// edit without needing a direct method call into the panel component.
const _pendingRenameLayer = signal<string | null>(null);
const _dragSelect = signal<DragSelectState>({
  active: false,
  startX: 0,
  startY: 0,
  currentX: 0,
  currentY: 0,
  mode: "inside",
});

// ---- History ----
const _history = signal<HistoryEntry[]>([]);
const _historyIndex = signal<number>(-1);
const _maxHistory = 50;

// ---- Layer color allocator ----
const _usedColors = signal<string[]>([]);

// ------- reader helpers -------
// Components pass these into their reactive contexts. They read the signal
// (which registers the dependency) and return a plain value.

export const entities = () => _entities.value;
export const layers = () => _layers.value;
export const layerOrder = () => _layerOrder.value;
export const selectedEntityIds = () => _selectedEntityIds.value;
export const selectedLayerNames = () => _selectedLayerNames.value;
export const selectedLayer = () => _selectedLayer.value;
export const originalDxf = () => _originalDxf.value;
export const filename = () => _filename.value;
export const customVars = () => _customVars.value;
export const loadedUnitCode = () => _loadedUnitCode.value;
export const healActive = () => _healActive.value;
export const healProgress = () => _healProgress.value;
export const glbActive = () => _glbActive.value;
export const glbProgress = () => _glbProgress.value;
export const glbUrl = () => _glbUrl.value;
export const viewMode = () => _viewMode.value;
export const resetSeq = () => _resetSeq.value;
// True when the currently-loaded DXF was cleaned by the fix_dxf pipeline
// (identified by the DXF_HEALED custom var). No user-facing Fix button any
// more — auto-heal on load owns the round-trip — but the flag still gates
// whether load-time heal fires.
export const isHealed = computed(() => _customVars.value["DXF_HEALED"] === "1");

export const dxfUnit = () => _dxfUnit.value;
export const dxfUnitCode = () => _dxfUnitCode.value;
export const viewerUnit = () => _viewerUnit.value;
export const gridUnit = () => _gridUnit.value;
export const gridSpacing = () => _gridSpacing.value;
export const showGrid = () => _showGrid.value;
export const showGridRuler = () => _showGridRuler.value;

export const zoom = () => _zoom.value;
export const referenceZoom = () => _referenceZoom.value;
export const pan = () => _pan.value;
export const bounds = () => _bounds.value;
export const isLoaded = () => _isLoaded.value;
export const viewSeq = () => _viewSeq.value;

export const theme = () => _theme.value;
export const filterText = () => _filterText.value;
export const sidebarPosition = () => _sidebarPosition.value;
export const sidebarVisible = () => _sidebarVisible.value;
export const hostWidth = () => _hostWidth.value;
export const isMobile = computed(() => _hostWidth.value <= MOBILE_BREAKPOINT);

export const toasts = () => _toasts.value;
export const toastsExpanded = () => _toastsExpanded.value;
export const modal = () => _modal.value;
export const contextMenu = () => _contextMenu.value;
export const dragSelect = () => _dragSelect.value;
export const pendingRenameLayer = () => _pendingRenameLayer.value;

export const canUndo = computed(() => _historyIndex.value > 0);
export const canRedo = computed(
  () => _historyIndex.value < _history.value.length - 1
);

// ------- mutation helpers -------
// Only `ops/` should import these. Grouped-writes go inside `batch(() => ...)`
// so subscribers see one update instead of N.

export const write = {
  entities: (v: Entity[]) => (_entities.value = v),
  layers: (v: Record<string, Layer>) => (_layers.value = v),
  layerOrder: (v: string[]) => (_layerOrder.value = v),
  selectedEntityIds: (v: Set<number>) => (_selectedEntityIds.value = v),
  selectedLayerNames: (v: Set<string>) => (_selectedLayerNames.value = v),
  selectedLayer: (v: string | null) => (_selectedLayer.value = v),
  nextEntityId: (v: number) => (_nextEntityId.value = v),
  originalDxf: (v: string | null) => (_originalDxf.value = v),
  filename: (v: string | null) => (_filename.value = v),
  customVars: (v: Record<string, string>) => (_customVars.value = v),
  loadedUnitCode: (v: number | null) => (_loadedUnitCode.value = v),
  healActive: (v: boolean) => (_healActive.value = v),
  healProgress: (v: number) => (_healProgress.value = v),
  glbActive: (v: boolean) => (_glbActive.value = v),
  glbProgress: (v: number) => (_glbProgress.value = v),
  glbUrl: (v: string | null) => (_glbUrl.value = v),
  viewMode: (v: "2d" | "3d") => (_viewMode.value = v),
  bumpReset: () => (_resetSeq.value = _resetSeq.value + 1),

  dxfUnit: (v: DxfUnit) => (_dxfUnit.value = v),
  dxfUnitCode: (v: number) => (_dxfUnitCode.value = v),
  viewerUnit: (v: DxfUnit) => (_viewerUnit.value = v),
  gridUnit: (v: DxfUnit) => (_gridUnit.value = v),
  gridSpacing: (v: number) => (_gridSpacing.value = v),
  showGrid: (v: boolean) => (_showGrid.value = v),
  showGridRuler: (v: boolean) => (_showGridRuler.value = v),

  zoom: (v: number) => (_zoom.value = v),
  referenceZoom: (v: number) => (_referenceZoom.value = v),
  pan: (v: Pan) => (_pan.value = v),
  bounds: (v: Bounds | null) => (_bounds.value = v),
  isLoaded: (v: boolean) => (_isLoaded.value = v),
  bumpView: () => (_viewSeq.value = _viewSeq.value + 1),

  theme: (v: Theme) => (_theme.value = v),
  filterText: (v: string) => (_filterText.value = v),
  sidebarPosition: (v: SidebarPosition) => (_sidebarPosition.value = v),
  sidebarVisible: (v: boolean) => (_sidebarVisible.value = v),
  hostWidth: (v: number) => {
    if (_hostWidth.value !== v) _hostWidth.value = v;
  },

  toasts: (v: ToastEntry[]) => (_toasts.value = v),
  toastsExpanded: (v: boolean) => (_toastsExpanded.value = v),
  modal: (v: ModalState) => (_modal.value = v),
  contextMenu: (v: ContextMenuState) => (_contextMenu.value = v),
  dragSelect: (v: DragSelectState) => (_dragSelect.value = v),
  pendingRenameLayer: (v: string | null) => (_pendingRenameLayer.value = v),

  usedColors: (v: string[]) => (_usedColors.value = v),
};

export const peek = {
  nextEntityId: () => _nextEntityId.peek(),
  nextToastId: () => {
    const id = _nextToastId.peek();
    _nextToastId.value = id + 1;
    return id;
  },
  toasts: () => _toasts.peek(),
  bumpEntityId: () => {
    const id = _nextEntityId.peek();
    _nextEntityId.value = id + 1;
    return id;
  },
  usedColors: () => _usedColors.peek(),
  history: () => _history.peek(),
  historyIndex: () => _historyIndex.peek(),
  entities: () => _entities.peek(),
  layers: () => _layers.peek(),
  layerOrder: () => _layerOrder.peek(),
  selectedEntityIds: () => _selectedEntityIds.peek(),
  selectedLayerNames: () => _selectedLayerNames.peek(),
  selectedLayer: () => _selectedLayer.peek(),
  bounds: () => _bounds.peek(),
  zoom: () => _zoom.peek(),
  pan: () => _pan.peek(),
  theme: () => _theme.peek(),
  sidebarVisible: () => _sidebarVisible.peek(),
  sidebarPosition: () => _sidebarPosition.peek(),
  showGrid: () => _showGrid.peek(),
  showGridRuler: () => _showGridRuler.peek(),
  gridSpacing: () => _gridSpacing.peek(),
  gridUnit: () => _gridUnit.peek(),
  dxfUnit: () => _dxfUnit.peek(),
  viewerUnit: () => _viewerUnit.peek(),
};

// ------- history -------
// Kept in the store because undo/redo needs the current history array too.
// Ops call `pushHistory` after a mutating action; the mixin drives undo/redo.

export function pushHistory(entry: HistoryEntry): void {
  batch(() => {
    const idx = _historyIndex.value;
    const truncated = _history.value.slice(0, idx + 1);
    truncated.push(entry);
    while (truncated.length > _maxHistory) truncated.shift();
    _history.value = truncated;
    _historyIndex.value = truncated.length - 1;
  });
}

export function stepHistory(delta: -1 | 1): HistoryEntry | null {
  const next = _historyIndex.value + delta;
  if (next < 0 || next >= _history.value.length) return null;
  _historyIndex.value = next;
  return _history.value[next] ?? null;
}

export function clearHistory(): void {
  batch(() => {
    _history.value = [];
    _historyIndex.value = -1;
  });
}

// Exposed only to the SignalWatcher mixin so it can hand raw signals to
// `effect()` for dependency tracking. Not for general use.
export const _internal = {
  signals: [
    _entities,
    _layers,
    _layerOrder,
    _selectedEntityIds,
    _selectedLayerNames,
    _selectedLayer,
    _customVars,
    _loadedUnitCode,
    _dxfUnit,
    _dxfUnitCode,
    _viewerUnit,
    _gridUnit,
    _gridSpacing,
    _showGrid,
    _showGridRuler,
    _zoom,
    _referenceZoom,
    _pan,
    _bounds,
    _isLoaded,
    _theme,
    _filterText,
  ] as Array<Signal<unknown>>,
};

export { batch };
